import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {Session} from 'nutshell-core/session';
import type {FilterObject, CoreEntityType, SavedFilter} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
// @ts-expect-error - imported from .js/.jsx file
import {DownloadIcon} from 'shells/icon/svg/download-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from 'shells/icon/svg/email-icon';

import type {GetAudienceForEntityList_audience as Audience} from 'nutshell-graphql-types';

import {openComposerWithId} from '../../email/email-composer-actions';
import type {SequenceTemplate} from '../../entity-list/email/bulk-email/bulk-email-modals/bulk-personal-email-modal';
import {SendFromListModal} from '../../entity-list/email/send-from-list-modal';
import {Notifies} from '../../entity-list/footer/notifies';
import {FilterShareButton} from '../../filtering/share-modal/filter-share-button';

type Props = {
    query: {
        filter: {
            selected: FilterObject[];
        };
        search: {
            selected: string;
        };
    };
    entityType: CoreEntityType;
    massSelect: boolean;
    selectedCount: number;
    selectedItems: string[];
    omittedItems: string[];
    onEmailSend: (
        template: SequenceTemplate,
        options: {
            includeIncomplete: boolean;
            includeRecentlyReceivedTemplate: boolean;
        }
    ) => void;
    exportSelectedItems: () => void;
    activeSavedFilter?: SavedFilter<any> | null | undefined;
    audience?: Audience | null | undefined;
    shouldShowExportAndEmailButtons: boolean;
    hideShareButton?: boolean;
};

export function EntityListTitleBarButtons(props: Props) {
    const {exportSelectedItems} = props;
    const dispatch = useDispatch();
    const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
    const validNotifies = useSelector((state) =>
        // @ts-expect-error
        Session.getSessionStateUserUnacknowedgedExportNotifications(state)
    );

    // @ts-expect-error
    const user = useSelector((state) => Session.getSessionStateUser(state));

    const handleExport = () => {
        // @ts-expect-error
        dispatch(Session.pollForQueuedExports());

        exportSelectedItems();
    };

    const handleEmailClick = () => {
        setIsEmailModalOpen(true);
    };

    const handleEmailClose = () => {
        setIsEmailModalOpen(false);
    };

    const handleOpenPersonalEmailComposer = () => {
        if (props.selectedItems.length === 1) {
            // @ts-expect-error
            dispatch(openComposerWithId(props.selectedItems[0], true));
        }
    };

    const userHasValidNotifies = validNotifies.length;

    return (
        <>
            {props.shouldShowExportAndEmailButtons && (
                <>
                    {userHasValidNotifies ? (
                        <div className='ui-entity-list-table-footer__notifies'>
                            <Notifies
                                notifies={validNotifies}
                                onAcknowledgeExportDownload={() => {
                                    dispatch(Session.requestFetchSession());
                                }}
                            />
                        </div>
                    ) : undefined}
                    {user && user.permissions.canExport && (
                        <Button
                            variant='secondary'
                            size='big'
                            onClick={handleExport}
                            icon={DownloadIcon}
                        >
                            Export
                        </Button>
                    )}
                    {user && user.permissions.canAccessCrm && (
                        <Button
                            variant='secondary'
                            size='big'
                            disabled={props.selectedCount === 0}
                            onClick={handleEmailClick}
                            icon={EmailIcon}
                        >
                            Email
                        </Button>
                    )}
                </>
            )}
            {!props.hideShareButton && (
                <FilterShareButton
                    savedFilter={props.activeSavedFilter}
                    audience={props.audience}
                />
            )}
            {isEmailModalOpen && user && (
                <SendFromListModal
                    onClose={handleEmailClose}
                    selectedCount={props.selectedCount}
                    canAccessMarketing={user.permissions.canAccessMarketing}
                    userEmail={
                        user.sendForUser && user.sendForUser.length
                            ? // @ts-expect-error
                              user.sendForUser[0].email
                            : undefined
                    }
                    handleOpenPersonalEmailComposer={handleOpenPersonalEmailComposer}
                    // Props for the bulk personal email modal
                    userId={user.id}
                    entityType={props.entityType}
                    query={props.query}
                    massSelect={props.massSelect}
                    selectedItems={props.selectedItems}
                    omittedItems={props.omittedItems}
                    onEmailSend={props.onEmailSend}
                    // Props for the marketing outreach email modal
                    numRecipients={props.selectedCount}
                    canAccessFullCampaigns={user.permissions.canAccessFullCampaigns}
                />
            )}
        </>
    );
}
